import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { toast } from '@/features/toast/toast'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type {
    BulkUpdateFileConnectionsData,
    BulkUpdateFileConnectionsRequest,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/files/bulk-update-connections'

type IBulkUpdateConnectionsPayload = BulkUpdateFileConnectionsRequest['body']

interface IBulkUpdateConnectionsArgs extends IBulkUpdateConnectionsPayload {
    organizationId: string
    brandSlug: string
}

type IBulkUpdateConnectionsMode = 'labels' | 'products'

class BulkUpdateConnectionsMutation {
    static call(api: IApi, args: IBulkUpdateConnectionsArgs) {
        const { organizationId, brandSlug, ...payload } = args

        return api
            .post<
                BulkUpdateFileConnectionsData,
                IBulkUpdateConnectionsPayload
            >(`/organization/${organizationId}/brands/${brandSlug}/files/bulk-update-connections`, payload)
            .then((res) => res.data)
    }
}

export function useBulkUpdateConnectionsMutation(mode: IBulkUpdateConnectionsMode) {
    const { api, apiParams, qc } = useApiToolbox()

    const strings =
        mode === 'labels'
            ? {
                  noChanges: 'No label changes were needed',
                  updated: (count: number) =>
                      count === 1 ? 'Updated labels for 1 asset' : `Updated labels for ${count} assets`,
                  updateError: 'Failed to update labels',
              }
            : {
                  noChanges: 'No product changes were needed',
                  updated: (count: number) =>
                      count === 1 ? 'Updated products for 1 asset' : `Updated products for ${count} assets`,
                  updateError: 'Failed to update products',
              }

    return useApiMutation<BulkUpdateFileConnectionsData, IBulkUpdateConnectionsPayload>({
        mutationFn: (payload) =>
            BulkUpdateConnectionsMutation.call(api, {
                ...payload,
                organizationId: apiParams.organizationId,
                brandSlug: apiParams.brandSlug,
            }),
        onSuccess: async (result) => {
            await qc.invalidateQueries({
                predicate: (query) => NODES_BASE_KEY.every((key, index) => query.queryKey[index] === key),
            })

            if (result.affectedFileCount === 0) {
                toast.success(strings.noChanges)
                return
            }

            toast.success(
                result.excludedNonImageCount > 0
                    ? `${strings.updated(result.affectedFileCount)} · ${result.excludedNonImageCount} non-image ${
                          result.excludedNonImageCount === 1 ? 'file' : 'files'
                      } skipped`
                    : strings.updated(result.affectedFileCount)
            )
        },
        onError: () => {
            toast.error(strings.updateError)
        },
    })
}
